import './GuidePanel.css';

import {
  Children,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Components } from 'react-markdown';
import { IonIcon } from '@ionic/react';
import {
  arrowBackOutline,
  arrowForwardOutline,
  arrowUpOutline,
  checkmarkCircle,
  chevronDownOutline,
  chevronUpOutline,
  closeOutline,
  playCircleOutline,
  playOutline,
} from 'ionicons/icons';
import clsx from 'clsx';
import Button from '../../Button/Button';
import MarkdownView from '../../Manager/GridInput/components/MarkdownView/MarkdownView';
import { useTranslate } from '../../../hooks/context/useTranslate';
import { useGuideContext } from '../../../context/GuideContext/GuideContext';
import { GuideDetail, GuideFieldHelp } from '../../../types/guides';
import { getGuideProgress } from '../../../utils/guides/guideProgressStore';
import {
  buildGuideInlineActionMap,
  normalizeGuideInlineLabel,
} from '../../../utils/guides/guideInlineActions';
import { activateGuideTarget } from '../../../utils/guides/resolveGuideTarget';

const COLLAPSED_FIELD_COUNT = 10;
const PANEL_ANIMATION_MS = 260;

type ContextHelpAnchor = { kind: 'field' | 'section'; name: string };

const escapeAttributeValue = (value: string): string =>
  typeof CSS !== 'undefined' && typeof CSS.escape === 'function'
    ? CSS.escape(value)
    : value.replace(/["\\]/g, '\\$&');

const inlineText = (value: ReactNode): string =>
  Children.toArray(value)
    .map((child) => {
      if (typeof child === 'string' || typeof child === 'number')
        return String(child);
      return '';
    })
    .join('');

// Layer B of Průvodce MySeptim: the agenda help panel opened by the "?" button.
// Renders inside .page-outlet so the sidebar stays usable while reading.
const GuidePanel = () => {
  const { t } = useTranslate();
  const {
    panelGuideId,
    currentScreenGuideId,
    closePanel,
    openPanelAtScreen,
    panelCanGoBack,
    panelCanGoForward,
    goBackPanel,
    goForwardPanel,
    loadGuide,
    ensureIndex,
    index,
    navigate,
    startTour,
    progress,
  } = useGuideContext();
  const [guide, setGuide] = useState<GuideDetail | null>(null);
  const [state, setState] = useState<'idle' | 'loading' | 'missing'>('idle');
  const [showAllFields, setShowAllFields] = useState(false);
  const [relatedOpen, setRelatedOpen] = useState(false);
  const [renderedGuideId, setRenderedGuideId] = useState<string | null>(
    panelGuideId
  );
  const [closing, setClosing] = useState(false);
  const [contextHelpAnchor, setContextHelpAnchor] =
    useState<ContextHelpAnchor | null>(null);
  const bodyRef = useRef<HTMLDivElement | null>(null);

  // AIR-432: panel scrolluje uvnitr .guide-panel-body, ne v dokumentu, takze
  // kotvy nejde resit pres href="#id" — scrollujeme rucne v tomto kontejneru.
  const scrollToSection = useCallback((sectionId: string) => {
    const body = bodyRef.current;
    if (!body) return;
    const target = body.querySelector<HTMLElement>(
      `[data-guide-anchor="${sectionId}"]`
    );
    if (!target) return;
    body.scrollTo({
      top: Math.max(0, target.offsetTop - body.offsetTop - 8),
      behavior: 'smooth',
    });
  }, []);

  const scrollToTop = useCallback(() => {
    bodyRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  useEffect(() => {
    if (panelGuideId) {
      setRenderedGuideId(panelGuideId);
      setClosing(false);
      return;
    }
    if (!renderedGuideId) return;
    setClosing(true);
    const timeout = window.setTimeout(() => {
      setRenderedGuideId(null);
      setGuide(null);
      setState('idle');
      setClosing(false);
    }, PANEL_ANIMATION_MS);
    return () => window.clearTimeout(timeout);
  }, [panelGuideId, renderedGuideId]);

  useEffect(() => {
    if (!renderedGuideId) return;
    let cancelled = false;
    setState('loading');
    setShowAllFields(false);
    setRelatedOpen(false);
    ensureIndex();
    loadGuide(renderedGuideId).then((loaded) => {
      if (cancelled) return;
      setGuide(loaded);
      setState(loaded ? 'idle' : 'missing');
    });
    return () => {
      cancelled = true;
    };
  }, [renderedGuideId, loadGuide, ensureIndex]);

  // When the current guide stays open, a click in the corresponding form moves
  // this panel to the field/section explanation. Only stable DOM ids participate.
  useEffect(() => {
    if (!guide || !panelGuideId || panelGuideId !== currentScreenGuideId)
      return;
    const handleContextClick = (event: MouseEvent) => {
      const target = event.target instanceof Element ? event.target : null;
      if (
        !target ||
        target.closest(
          '[data-guide-panel], [data-guide-launcher], [data-guide-start-banner]'
        )
      ) {
        return;
      }
      const fieldElement = target.closest<HTMLElement>(
        '[data-form-field], [data-column-name]'
      );
      const fieldName =
        fieldElement?.getAttribute('data-form-field') ??
        fieldElement?.getAttribute('data-column-name');
      if (fieldName) {
        const fieldIndex = guide.fields.findIndex(
          (field) =>
            field.name.toLocaleLowerCase() === fieldName.toLocaleLowerCase()
        );
        if (fieldIndex >= 0) {
          if (fieldIndex >= COLLAPSED_FIELD_COUNT) setShowAllFields(true);
          setContextHelpAnchor({
            kind: 'field',
            name: guide.fields[fieldIndex].name,
          });
          return;
        }
      }
      const sectionElement = target.closest<HTMLElement>(
        '[data-form-section], [data-guide-form-section-trigger]'
      );
      const sectionName =
        sectionElement?.getAttribute('data-form-section') ??
        sectionElement?.getAttribute('data-guide-form-section-trigger');
      const sectionFieldIndex = sectionName
        ? guide.fields.findIndex((field) => field.sectionId === sectionName)
        : -1;
      if (sectionName && sectionFieldIndex >= 0) {
        if (sectionFieldIndex >= COLLAPSED_FIELD_COUNT) setShowAllFields(true);
        setContextHelpAnchor({ kind: 'section', name: sectionName });
      }
    };
    document.addEventListener('click', handleContextClick);
    return () => document.removeEventListener('click', handleContextClick);
  }, [guide, panelGuideId, currentScreenGuideId]);

  useEffect(() => {
    if (!contextHelpAnchor) return;
    const body = bodyRef.current;
    if (!body) return;
    const attribute =
      contextHelpAnchor.kind === 'field'
        ? 'data-guide-field-anchor'
        : 'data-guide-section-anchor';
    const selector = `[${attribute}="${escapeAttributeValue(contextHelpAnchor.name)}"]`;
    const frame = window.requestAnimationFrame(() => {
      const target = body.querySelector<HTMLElement>(selector);
      if (!target) return;
      body.scrollTo({
        top: Math.max(0, target.offsetTop - body.offsetTop - 8),
        behavior: 'smooth',
      });
      target.classList.add('guide-context-help-active');
      window.setTimeout(
        () => target.classList.remove('guide-context-help-active'),
        1800
      );
      setContextHelpAnchor(null);
    });
    return () => window.cancelAnimationFrame(frame);
  }, [contextHelpAnchor, showAllFields]);

  const guideTitles = useMemo(() => {
    const map = new Map<string, string>();
    index?.guides.forEach((item) => map.set(item.id, item.title));
    return map;
  }, [index]);

  // Poradi musi odpovidat poradi bloku v panelu, jinak obsah klame.
  const tocItems = useMemo(() => {
    if (!guide) return [] as Array<{ id: string; label: string }>;
    const items: Array<{ id: string; label: string }> = [];
    if (guide.tours.length > 0)
      items.push({ id: 'tours', label: t('guide.tours') });
    guide.sections.forEach((section) => {
      items.push({ id: `sec-${section.id}`, label: section.title });
    });
    if ((guide.fields ?? []).length > 0)
      items.push({ id: 'fields', label: t('guide.fields') });
    return items;
  }, [guide, t]);

  const inlineActions = useMemo(
    () => (guide ? buildGuideInlineActionMap(guide, index) : new Map()),
    [guide, index]
  );

  const mainPathSteps = index?.paths[0]?.steps ?? [];
  const mainStepLabel = (title: string) => title.replace(/^\d+\.\s*/, '');
  const selectedMainStep = mainPathSteps.some(
    (step) => step.guideId === renderedGuideId
  )
    ? (renderedGuideId ?? '')
    : '';

  const runInlineAction = useCallback(
    async (label: string) => {
      const item = inlineActions.get(normalizeGuideInlineLabel(label));
      if (!item) return;
      if (item.guideId && item.guideId !== renderedGuideId) {
        openPanelAtScreen(item.guideId);
        return;
      }
      if (item.target) {
        navigate(item.action, () => {
          // Let React replace the previous route before resolving common field names.
          window.setTimeout(() => void activateGuideTarget(item.target!), 160);
        });
        return;
      }
      navigate(item.action);
    },
    [inlineActions, navigate, openPanelAtScreen, renderedGuideId]
  );

  const markdownComponents = useMemo<Components>(
    () => ({
      strong: ({ children }) => {
        const label = inlineText(children);
        if (!inlineActions.has(normalizeGuideInlineLabel(label)))
          return <strong>{children}</strong>;
        return (
          <button
            type="button"
            className="guide-inline-action"
            onClick={() => void runInlineAction(label)}
          >
            {children}
          </button>
        );
      },
    }),
    [inlineActions, runInlineAction]
  );

  if (!renderedGuideId) return null;

  const fields = guide?.fields ?? [];
  const visibleFields = showAllFields
    ? fields
    : fields.slice(0, COLLAPSED_FIELD_COUNT);
  // Group like the form: one block per section (tab) in field order; ungrouped fields keep their place.
  const fieldGroups = visibleFields.reduce<
    Array<{ section?: string; sectionId?: string; fields: GuideFieldHelp[] }>
  >((groups, field) => {
    const last = groups[groups.length - 1];
    if (
      last &&
      last.section === field.section &&
      last.sectionId === field.sectionId
    ) {
      last.fields.push(field);
    } else {
      groups.push({
        section: field.section,
        sectionId: field.sectionId,
        fields: [field],
      });
    }
    return groups;
  }, []);

  return (
    <aside
      className={clsx(
        'guide-panel',
        closing ? 'guide-panel-closing' : 'guide-panel-opening'
      )}
      role="complementary"
      aria-label={t('guide.panelTitle')}
      data-guide-panel
    >
      <div className="guide-panel-head">
        <div className="guide-panel-topline">
          <div className="guide-panel-heading">
            <div className="guide-panel-eyebrow">{t('guide.panelTitle')}</div>
            <h2 className="guide-panel-title">
              {guide?.title ??
                (state === 'loading' ? t('guide.loading') : t('guide.help'))}
              {guide?.status === 'draft' && (
                <span className="guide-badge guide-badge-draft">
                  {t('guide.draft')}
                </span>
              )}
            </h2>
          </div>
          <div className="guide-panel-navigation">
            <button
              type="button"
              className="guide-panel-icon-button"
              onClick={goBackPanel}
              disabled={!panelCanGoBack}
              aria-label={t('guide.historyBack')}
              title={t('guide.historyBack')}
            >
              <IonIcon icon={arrowBackOutline} />
            </button>
            <button
              type="button"
              className="guide-panel-icon-button"
              onClick={goForwardPanel}
              disabled={!panelCanGoForward}
              aria-label={t('guide.historyForward')}
              title={t('guide.historyForward')}
            >
              <IonIcon icon={arrowForwardOutline} />
            </button>
            <button
              type="button"
              className="guide-panel-icon-button guide-panel-close"
              onClick={closePanel}
              aria-label={t('guide.close')}
              title={t('guide.close')}
            >
              <IonIcon icon={closeOutline} />
            </button>
          </div>
        </div>
        {guide && guide.related.length > 0 && (
          <div className="guide-related">
            <button
              type="button"
              className="guide-related-toggle"
              aria-expanded={relatedOpen}
              onClick={() => setRelatedOpen((value) => !value)}
            >
              {t('guide.related')}
              <IonIcon
                icon={relatedOpen ? chevronUpOutline : chevronDownOutline}
              />
            </button>
            {relatedOpen && (
              <div className="guide-chips">
                {guide.related.map((relatedId) => (
                  <button
                    key={relatedId}
                    type="button"
                    className="guide-chip"
                    disabled={!guideTitles.has(relatedId)}
                    onClick={() => openPanelAtScreen(relatedId)}
                  >
                    {guideTitles.get(relatedId) ?? relatedId}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
        {mainPathSteps.length > 0 && (
          <div className="guide-main-step-field">
            <label htmlFor="guide-main-step-select">
              {t('guide.chooseMainStep')}
            </label>
            <select
              id="guide-main-step-select"
              className="guide-main-step-select"
              value={selectedMainStep}
              onChange={(event) => {
                if (event.target.value) openPanelAtScreen(event.target.value);
              }}
              data-guide-main-step-select
            >
              {mainPathSteps.map((step) => (
                <option key={step.id} value={step.guideId}>
                  {mainStepLabel(step.title)}
                </option>
              ))}
            </select>
          </div>
        )}
      </div>

      <div className="guide-panel-body" ref={bodyRef}>
        {state === 'missing' && (
          <p className="guide-panel-muted">{t('guide.notAvailable')}</p>
        )}

        {guide && (
          <>
            {guide.summary && (
              <p className="guide-panel-summary">{guide.summary}</p>
            )}

            {tocItems.length > 1 && (
              <nav
                className="guide-toc"
                aria-label={t('guide.tableOfContents')}
              >
                <div className="guide-toc-title">
                  {t('guide.tableOfContents')}
                </div>
                <ul className="guide-toc-list">
                  {tocItems.map((item) => (
                    <li key={item.id}>
                      <button
                        type="button"
                        className="guide-toc-link"
                        onClick={() => scrollToSection(item.id)}
                      >
                        {item.label}
                      </button>
                    </li>
                  ))}
                </ul>
              </nav>
            )}

            {guide.tours.length > 0 && (
              <section
                className="guide-panel-section"
                data-guide-anchor="tours"
              >
                <h3>{t('guide.tours')}</h3>
                <ul className="guide-tour-list">
                  {guide.tours.map((tour) => {
                    const tourProgress = getGuideProgress(
                      progress,
                      'tour',
                      tour.id
                    );
                    return (
                      <li key={tour.id} className="guide-tour-item">
                        <div className="guide-tour-item-text">
                          <div className="guide-tour-item-title">
                            {tourProgress?.status === 'completed' && (
                              <IonIcon
                                icon={checkmarkCircle}
                                className="guide-done-icon"
                              />
                            )}
                            {tour.title}
                          </div>
                          <div className="guide-panel-muted guide-tour-item-meta">
                            {tour.stepCount} {t('guide.steps')}
                            {tour.estimatedMinutes !== undefined &&
                              ` · ${tour.estimatedMinutes} ${t('guide.minutes')}`}
                          </div>
                        </div>
                        <div className="guide-tour-item-actions">
                          <Button
                            small
                            fill="solid"
                            onClick={() => startTour(tour.id, 'interactive')}
                          >
                            <IonIcon icon={playOutline} slot="start" />
                            {t('guide.start')}
                          </Button>
                          <Button
                            small
                            fill="outline"
                            onClick={() => startTour(tour.id, 'autoplay')}
                            title={t('guide.play')}
                          >
                            <IonIcon icon={playCircleOutline} slot="start" />
                            {t('guide.play')}
                          </Button>
                        </div>
                      </li>
                    );
                  })}
                </ul>
                <button
                  type="button"
                  className="guide-back-to-top"
                  onClick={scrollToTop}
                >
                  <IonIcon icon={arrowUpOutline} />
                  {t('guide.backToTop')}
                </button>
              </section>
            )}

            {guide.sections.map((section) => (
              <section
                key={section.id}
                className="guide-panel-section"
                data-guide-anchor={`sec-${section.id}`}
              >
                <h3>{section.title}</h3>
                <MarkdownView
                  value={section.body}
                  className="guide-markdown"
                  components={markdownComponents}
                />
                <button
                  type="button"
                  className="guide-back-to-top"
                  onClick={scrollToTop}
                >
                  <IonIcon icon={arrowUpOutline} />
                  {t('guide.backToTop')}
                </button>
              </section>
            ))}

            {fields.length > 0 && (
              <section
                className="guide-panel-section"
                data-guide-anchor="fields"
              >
                <h3>{t('guide.fields')}</h3>
                {fieldGroups.map((group, index) => (
                  <div
                    key={`${group.sectionId ?? group.section ?? 'ungrouped'}-${index}`}
                    className="guide-field-group"
                    data-guide-section-anchor={group.sectionId}
                  >
                    {group.section && (
                      <h4 className="guide-field-group-title">
                        {group.section}
                      </h4>
                    )}
                    <dl className="guide-fields">
                      {group.fields.map((field) => (
                        <div
                          key={field.name}
                          className="guide-field"
                          data-guide-field-anchor={field.name}
                        >
                          <dt>{field.label ?? field.name}</dt>
                          <dd
                            className={clsx(!field.help && 'guide-panel-muted')}
                          >
                            {field.help ? (
                              <MarkdownView
                                value={field.help}
                                className="guide-markdown guide-markdown-inline"
                                components={markdownComponents}
                              />
                            ) : (
                              t('guide.fieldHelpMissing')
                            )}
                          </dd>
                        </div>
                      ))}
                    </dl>
                  </div>
                ))}
                {fields.length > COLLAPSED_FIELD_COUNT && (
                  <button
                    type="button"
                    className="guide-link-button"
                    onClick={() => setShowAllFields((value) => !value)}
                  >
                    {showAllFields
                      ? t('guide.showFewerFields')
                      : t('guide.showAllFields')}
                  </button>
                )}
                <button
                  type="button"
                  className="guide-back-to-top"
                  onClick={scrollToTop}
                >
                  <IonIcon icon={arrowUpOutline} />
                  {t('guide.backToTop')}
                </button>
              </section>
            )}
          </>
        )}
      </div>

      <div className="guide-panel-foot">
        <button
          type="button"
          className="guide-link-button"
          onClick={() =>
            navigate(
              { type: 'navigate', route: 'guides', params: {} },
              closePanel
            )
          }
        >
          {t('guide.allGuides')}
        </button>
      </div>
    </aside>
  );
};

export default GuidePanel;
